import React from "react";
import { APP_BACK_TRANSIENT_PRIORITY, registerAppBackHandler } from "@app/back";
import { ActionSheet, type ActionSheetProps } from "@shared/ui/overlay/action-sheet";
import { withMobileBottomSheetShell } from "../mobileBottomSheetShell";
import { useWindows } from "./useWindows";
import type { OpenWindowOptions, OverlayHandle } from "../types";

const MobileActionSheet = withMobileBottomSheetShell(ActionSheet, {
    title: (props) => props.title,
    supportingTitle: (props) => props.supportingTitle,
});

/** Опции открытия action sheet. */
export interface OpenActionSheetOptions extends Omit<OpenWindowOptions, "blocking" | "placement"> {
    /** Заголовок shell-панели. */
    title?: React.ReactNode;
    /** Дополнительный текст shell-панели. */
    supportingTitle?: React.ReactNode;
}

/** API открытия mobile action sheet. */
export interface ActionSheetApi {
    /**
     * Открывает action sheet в общем overlay stack.
     * @param props контент и секции action sheet
     * @param options overlay-опции и shell-заголовок
     */
    open: (props: ActionSheetProps, options?: OpenActionSheetOptions) => OverlayHandle;
}

/**
 * Hook для коротких mobile action sheets: команды, toggles и лёгкий drill-in поверх текущего экрана.
 */
export function useActionSheet(): ActionSheetApi {
    const windows = useWindows();

    const open = React.useCallback<ActionSheetApi["open"]>(
        (props, options = {}) => {
            const { title, supportingTitle, ...overlayOptions } = options;
            const nextProps: ActionSheetProps = {
                ...props,
                title: props.title ?? title,
                supportingTitle: props.supportingTitle ?? supportingTitle,
                registerBackHandler: (handler) => registerAppBackHandler(handler, { priority: APP_BACK_TRANSIENT_PRIORITY }),
            };

            return windows.open(MobileActionSheet, nextProps, {
                blocking: true,
                placement: "bottom",
                closeOnBackdropClick: true,
                reuse: "replace",
                ...overlayOptions,
                key: overlayOptions.key ?? "mobile-action-sheet",
            });
        },
        [windows],
    );

    return React.useMemo(() => ({ open }), [open]);
}
